import React, { PureComponent } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { monthName, monthShortName } from '../../../shared/constants';

import Icon from '../Icon/Icon';
import DateSelectorContent from './DateSelectorContent';
import ConsoleLog from './../../../hoc/ConsoleLog/ConsoleLog';

const DateSelectorContainer = styled.div`
  position: relative;
  font-family: Roboto;
`;

const DateSelectorFormGroup = styled.span`
  position: relative;
  border: none;
  overflow: hidden;
`;

const DateSelectorLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #323c47;
  display:block;
`;

const DateSelectorWrapper = styled.div`
  float: left;
`;

const DateSelectorInput = styled.input`
  padding: 10px 12px;
  padding-right: 0;
  font-size: 12px;
  font-weight: 500;
  color: #323c47;
  width: 95px;
  height: 44px;
  border-radius: 4px;
  border: solid 1px #e9eff4;
  border-right:0;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  &::placeholder {
    font-size: 12px;
  }
  @media(min-width: 800px) {
    padding: 10px 20px;
    font-size: 14px;
    width: 154px;
  }
`;

const DateSelectorIcon = styled.div`
  width: 45px;
  height: 44px;
  border-radius: 4px;
  border: solid 1px #e9eff4;
  background-color: rgba(244, 247, 249, 0.4);
  float: right;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  box-sizing: border-box;
  position: relative;
  padding: 14px;
  &:hover {
    cursor: pointer;
  }

  @media(max-width: 768px) {
    &.ui-icon>svg{
      width: 12px;
    }
  }
`;

const IconStyle = styled(Icon)`
  svg {
    width: 12px;
    height: 14px;

    @media(min-width: 800px) {      
      width: 16px;
      height: 18px;
    }
  }
`;

class DateSelector extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      calendarOpen: false,
      current: this.props.date,
      today: (this.props.date && this.props.date !== null) ? null : new Date(),
      date: (this.props.date) ? this.props.date : new Date(),
      currentDate: (this.props.date && this.props.date !== null) ? this.handleDateFormat(this.props.date) : ''
    }

    this.setCalendarRef = this.setCalendarRef.bind(this);
  }

  componentWillUpdate(nextProps, prevState) {
    if (nextProps.date !== this.props.date) {
      //ConsoleLog("componentWillUpdate",nextProps.date,this.props.date)
      this.setState({ currentDate: this.handleDateFormat(nextProps.date) });
    }
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  };

  setCalendarRef(node) {
    this.calendarRef = node;
  }

  getWeekDays = () => {
    const currentDay = this.state.date;
    const weekDay = (currentDay.getDay() === 0) ? 7 : currentDay.getDay();
    const prevWeek = currentDay * 1 - (weekDay - 1) * 24 * 3600 * 1000;
    const nextWeek = currentDay * 1 + (8 - weekDay) * 24 * 3600 * 1000;

    var oneDay = 24 * 3600 * 1000;
    for (var d = [], ms = prevWeek * 1, last = nextWeek * 1; ms < last; ms += oneDay) {
      d.push(new Date(ms));
    }
    return d;
  }

  toggleCalendar = () => {
    this.setState({ calendarOpen: !this.state.calendarOpen });
  }

  isDate = date => {
    const isDate = Object.prototype.toString.call(date) === '[object Date]';
    const isValidDate = date && !Number.isNaN(date.valueOf());

    return isDate && isValidDate;
  }

  isSameDay = (date, basedate = new Date()) => {
    if (!(this.isDate(date) && this.isDate(basedate))) return false;

    const basedateDate = basedate.getDate();
    const basedateMonth = +(basedate.getMonth()) + 1;
    const basedateYear = basedate.getFullYear();

    const dateDate = date.getDate();
    const dateMonth = +(date.getMonth()) + 1;
    const dateYear = date.getFullYear();

    return (+basedateDate === +dateDate) && (+basedateMonth === +dateMonth) && (+basedateYear === +dateYear);
  }

  handlePreviousMonth = (event) => {
    event.preventDefault();
    if (!this.props.maxWeeks || this.props.maxWeeks > 4) {
      const date = this.state.date;
      const prevMonth = new Date(date.setMonth(date.getMonth() - 1));
      this.setState({ date: prevMonth });
    }
  }

  handleNextMonth = (event) => {
    event.preventDefault();
    if (!this.props.maxWeeks || this.props.maxWeeks > 4) {
      const date = this.state.date;
      const nextMonth = new Date(date.setMonth(date.getMonth() + 1));
      this.setState({ date: nextMonth });
    }
  }

  handlePreviousWeek = (event) => {
    const date = this.state.date;
    const today = new Date();
    const weekDay = (date.getDay() === 0) ? 7 : date.getDay();

    const dateCopy = new Date(date.getFullYear() + '/' + parseInt(date.getMonth() + 1) + '/' + date.getDate());
    const firstDayWeek = new Date(dateCopy.setDate(dateCopy.getDate() - (weekDay)));

    if (firstDayWeek > today) {
      const prevWeek = new Date(date.setDate(date.getDate() - 7));
      this.setState({ date: prevWeek });
    }
  }

  handleNextWeek = (event) => {
    const date = this.state.date;
    const today = new Date();
    const weekDay = (date.getDay() === 0) ? 7 : date.getDay();
    const dateCopy = new Date(date.getFullYear() + '/' + parseInt(date.getMonth() + 1) + '/' + date.getDate());
    const lastDayWeek = new Date(dateCopy.setDate(dateCopy.getDate() + (7 - weekDay)));
    const maxDate = new Date(today.setDate(today.getDate() + (this.props.maxWeeks * 7)));
    if (maxDate > lastDayWeek) {
      const nextWeek = new Date(date.setDate(date.getDate() + 7));
      this.setState({ date: nextWeek });
    }
  }

  handleSelect = (selectedDate) => {
    const today = new Date();
    today.setHours(0);
    today.setMinutes(0);
    today.setSeconds(0);


    if (selectedDate >= today) {
      this.setState({
        current: selectedDate,
        currentDate: this.handleDateFormat(selectedDate),
        today: null,
        calendarOpen: false
      });
      this.props.handleSelect(selectedDate);
    }
  }

  handleDateFormat = (selectedDate) => {
    const format = this.props.format.toLowerCase();
    switch (format) {
      case 'dd/mm/yyyy':
        return selectedDate.getDate() + "/" + parseInt(selectedDate.getMonth() + 1) + "/" + selectedDate.getFullYear();
      case 'mm/dd/yyyy':
        return parseInt(selectedDate.getMonth() + 1) + "/" + selectedDate.getDate() + "/" + selectedDate.getFullYear();
      case 'dd mmm yyyy':
        return selectedDate.getDate() + " " + monthShortName[selectedDate.getMonth()] + " " + selectedDate.getFullYear();
      default:
        return selectedDate.getDate() + "/" + parseInt(selectedDate.getMonth() + 1) + "/" + selectedDate.getFullYear();
    }
  }

  handleClickOutside = (event) => {
    if (this.calendarRef && !this.calendarRef.contains(event.target)) {
      this.setState({ calendarOpen: false });
    }
  }

  render() {
    return (
      <DateSelectorContainer ref={this.setCalendarRef}>
        <DateSelectorFormGroup>
          <DateSelectorLabel>{'Date'}</DateSelectorLabel>
          <DateSelectorWrapper onClick={this.toggleCalendar}>
            <DateSelectorInput
              type="text"
              value={this.state.currentDate}
              placeholder={this.props.format}
              readOnly="readonly"
            />
            <DateSelectorIcon>
              <IconStyle icon={"calendar-icon"} />
            </DateSelectorIcon>
          </DateSelectorWrapper>
        </DateSelectorFormGroup>
        <DateSelectorContent
          calendarOpen={this.state.calendarOpen}
          monthYear={monthName[this.state.date.getMonth()] + ' ' + this.state.date.getFullYear()}
          handlePreviousMonth={this.handlePreviousMonth}
          handleNextMonth={this.handleNextMonth}
          handlePreviousWeek={this.handlePreviousWeek}
          handleNextWeek={this.handleNextWeek}
          getWeekDays={this.getWeekDays}
          isSameDay={this.isSameDay}
          current={this.state.current}
          handleSelect={this.handleSelect}
          today={this.state.today}
          leftPosition={this.props.leftPosition}
        />
      </DateSelectorContainer>
    );
  }
}

DateSelector.propTypes = {
  date: PropTypes.object,
  format: PropTypes.string,
  handleSelect: PropTypes.func,
  maxWeeks: PropTypes.number,
  disablePreviousDates: PropTypes.bool
};

DateSelector.defaultProps = {
  date: null,
  format: 'dd mmm yyyy',
  maxWeeks: 2,
  disablePreviousDates: false
};

export default DateSelector;


// WEBPACK FOOTER //
// ./src/components/UI/DateSelector/DateSelector.js